// Settings → Telephony tab.
//
// Source-of-truth: /var/www/kitchenos/portal-standalone.html
//   tab button:          line 2752  (#settings-panel-telephony)
//   renderTelephonyPanel line 32568
//   kosDownloadVCard     line 32620
//
// Backend
// -------
// The retailer-callable endpoint that returns the assigned platform number is:
//
//   GET /api/calls/assignment?retailerId=<id>
//
// (kitchenos-api/modules/calls.js — `requirePortal` middleware, soft-auth).
// The response shape used by the legacy portal is:
//
//   { assignment: { platformNumber: "+447446961564", retailerMobile: "...", ... } }
//
// Call-forwarding / call-recording / voicemail-greeting endpoints are NOT
// implemented today (verified against /var/www/kitchenos-api/modules/calls.js
// and modules/twilio.js as of 2026-05). We render the controls as the master
// spec requires but flag the saves as "saved locally — server endpoint
// pending" so the demo UX works while the backend catches up.
//
//   TODO: PATCH /api/portal/twilio/forwarding       { forwardTo, enabled }
//   TODO: PATCH /api/portal/twilio/recording        { enabled }
//   TODO: PUT   /api/portal/twilio/voicemail        { greeting }

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import { getBranding } from "../../../shell/branding";
import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";

import "./telephony.css";

// ── Types ────────────────────────────────────────────────────────────────────

interface CallAssignmentResponse {
  assignment?: {
    platformNumber?: string;
    retailerMobile?: string;
  };
  // The legacy portal also tolerated `__httpStatus` / `body` wrappers but the
  // typed api() helper rejects non-2xx responses before they reach us so we
  // don't need to handle those shapes here.
}

type ForwardCondition = "always" | "busy" | "no_answer" | "busy_no_answer";

interface TwilioSettings {
  forwardTo: string;
  forwardEnabled: boolean;
  forwardCondition: ForwardCondition;
  recordingEnabled: boolean;
  voicemailGreeting: string;
}

interface ForwardedCall { ts: string; from: string; to: string; durationSec: number; costGbp: number }

const FALLBACK_NUMBER = "+447307230072"; // ← matches portal-standalone.html:32572

/** The pre-filled default for the (editable) voicemail greeting.
 *
 *  This plays to the RETAILER'S OWN CUSTOMERS, so it must never name the
 *  platform — a retailer who never edits it would otherwise be answering their
 *  callers in our name. It is therefore built from their business name at
 *  render time rather than being a fixed string.
 *
 *  `usingPlatformDefault` (not a string comparison) is what tells us whether
 *  the profile carries a real business name; when it doesn't, we drop the name
 *  from the sentence entirely rather than substituting the platform brand. */
function defaultVoicemail(): string {
  const b = getBranding();
  const opener = b.usingPlatformDefault || !b.businessName.trim()
    ? "Hi, thanks for calling."
    : `Hi, you've reached ${b.businessName.trim()}.`;
  return `${opener} We're not available right now — please ` +
    "leave a short message after the tone and we'll call you straight back.";
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function isMissingEndpoint(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

/**
 * Format a +44 E.164 mobile number as "07XXX XXXXXX" for display, matching
 * the standalone portal's formatter. Falls back to the raw string for
 * non-UK numbers so we never show garbage.
 */
function formatNumber(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "Not assigned";
  if (/^\+44\d{10}$/.test(trimmed)) {
    const local = "0" + trimmed.slice(3);
    return local.replace(/^(\d{5})(\d{6})$/, "$1 $2");
  }
  return trimmed;
}

function buildVCard(phone: string): string {
  // Newlines must be CRLF per RFC 6350; most clients accept LF too but
  // CRLF gives us the widest device compatibility.
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    "FN:Kitchengine",
    "ORG:Kitchengine",
    "TEL;TYPE=WORK,VOICE:" + phone,
    "END:VCARD",
  ].join("\r\n");
}

function esc(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Tab definition ───────────────────────────────────────────────────────────

const telephonyTab: SettingsTab = {
  id: "telephony",
  label: "Telephony",
  icon: iconMarkup("phone", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext) {
    const ac = new AbortController();
    let disposed = false;

    // Track every object URL we create so cleanup can revoke them — the
    // vCard download blob URLs leak memory in long sessions otherwise.
    const objectUrls = new Set<string>();

    // Per-control debounce timers (forwardTo + voicemailGreeting use these so
    // typing doesn't fire a save on every keystroke).
    const debounceTimers = new Map<string, number>();

    // Working state for the non-Twilio controls. Seeded from defaults; would
    // be hydrated from `GET /api/portal/twilio/settings` once that exists.
    const settings: TwilioSettings = {
      forwardTo: "",
      forwardEnabled: false,
      forwardCondition: "busy_no_answer",
      recordingEnabled: false,
      voicemailGreeting: defaultVoicemail(),
    };

    // Verified numbers on the account (platform number, registered mobile,
    // admin-approved extras) + recent forwarded-call log. Hydrated from the
    // server on boot.
    let verifiedNumbers: string[] = [];
    let forwardedLog: ForwardedCall[] = [];

    // ── Scaffold ──────────────────────────────────────────────────────────
    const root = document.createElement("section");
    root.className = "set-tel-root";
    root.innerHTML = renderLoadingHtml();
    container.appendChild(root);

    function renderLoadingHtml(): string {
      return `<div class="set-tel-loading">Loading platform number…</div>`;
    }

    function renderPanel(platformNumber: string): void {
      const formatted = formatNumber(platformNumber);
      const hasNumber = !!platformNumber;
      root.innerHTML = `
        <h3 class="set-section-title set-tel-section-title">Platform Telephony</h3>

        <div class="set-tel-card">
          <div class="set-tel-card__label">Your Platform Number</div>
          <div class="set-tel-card__number" data-role="number">${esc(formatted)}</div>
          <p class="set-tel-card__copy">
            Save this number in your phone as <b>"Kitchengine"</b> — this is the
            number your customers will see when you call them through the platform.
            Any calls back to this number will route directly to you.
          </p>
          ${
            hasNumber
              ? `
            <button
              type="button"
              class="set-tel-vcard-btn"
              data-action="save-contact"
            >
              ${iconMarkup("upload", { size: "sm" })}
              Save to Contacts
            </button>
          `
              : ""
          }
        </div>

        <aside class="set-tel-tip" role="note">
          ${iconMarkup("info", { size: "sm" })}
          <div>
            <b>Pro Tip:</b> When your mobile rings from this number, it means a
            customer is being connected. Answer it to start the bridge.
          </div>
        </aside>

        <section class="set-tel-subsection">
          <h4 class="set-tel-subsection__title">Call forwarding</h4>
          <p class="set-help">
            Forward incoming customer calls to another number when you're busy or
            don't answer. For your protection, calls can only forward to a number
            already verified on your account — any new number is sent to
            Kitchengine for approval first. Every forwarded call is logged with its
            duration and cost.
          </p>
          <label class="set-tel-switch">
            <input
              type="checkbox"
              data-role="forward-toggle"
              ${settings.forwardEnabled ? "checked" : ""}
            />
            <span class="set-tel-switch__track"></span>
            <span class="set-tel-switch__label">Forward calls when I'm unavailable</span>
          </label>
          <div class="set-tel-field" data-role="forward-field" ${settings.forwardEnabled ? "" : "hidden"}>
            <div class="set-field">
              <label for="set-tel-forward-when">When to forward</label>
              <select id="set-tel-forward-when" class="set-select" data-role="forward-condition">
                <option value="busy_no_answer"${settings.forwardCondition === "busy_no_answer" ? " selected" : ""}>When busy or no answer</option>
                <option value="busy"${settings.forwardCondition === "busy" ? " selected" : ""}>When busy</option>
                <option value="no_answer"${settings.forwardCondition === "no_answer" ? " selected" : ""}>When no answer</option>
                <option value="always"${settings.forwardCondition === "always" ? " selected" : ""}>Always</option>
              </select>
            </div>
            <div class="set-field">
              <label for="set-tel-forward-input">Forward to</label>
              <input
                id="set-tel-forward-input"
                type="tel"
                class="set-input"
                list="set-tel-verified-numbers"
                placeholder="+44 7700 900000"
                value="${esc(settings.forwardTo)}"
                data-role="forward-input"
                autocomplete="tel"
              />
              <datalist id="set-tel-verified-numbers">
                ${verifiedNumbers.map((n) => `<option value="${esc(n)}"></option>`).join("")}
              </datalist>
              <p class="set-help" data-role="forward-hint">
                ${verifiedNumbers.length
                  ? `Verified numbers: ${verifiedNumbers.map(esc).join(", ")}. Any other number needs admin approval before it routes calls.`
                  : `No verified numbers on your account yet — a new number will be sent for approval.`}
              </p>
            </div>
          </div>
          <div class="set-tel-fwd-log" data-role="forward-log" ${forwardedLog.length ? "" : "hidden"}>
            <h5 class="set-tel-fwd-log__title">Recent forwarded calls</h5>
            <ul class="set-tel-fwd-log__list">
              ${forwardedLog.slice(0, 8).map((c) => {
                const mins = Math.floor(c.durationSec / 60);
                const secs = c.durationSec % 60;
                const dur = `${mins}:${String(secs).padStart(2, "0")}`;
                return `<li><span>${esc(c.to)}</span><span>${dur}</span><span>£${c.costGbp.toFixed(2)}</span></li>`;
              }).join("")}
            </ul>
          </div>
        </section>

        <section class="set-tel-subsection">
          <h4 class="set-tel-subsection__title">Call recording</h4>
          <p class="set-help">
            Record outbound platform calls for quality and dispute resolution.
            Recordings are kept for 30 days, then auto-deleted.
          </p>
          <label class="set-tel-switch">
            <input
              type="checkbox"
              data-role="recording-toggle"
              ${settings.recordingEnabled ? "checked" : ""}
            />
            <span class="set-tel-switch__track"></span>
            <span class="set-tel-switch__label">Record outbound calls</span>
          </label>
        </section>

        <section class="set-tel-subsection">
          <h4 class="set-tel-subsection__title">Voicemail greeting</h4>
          <p class="set-help">
            Played to customers when you can't take their call. Keep it short and
            mention you'll call back.
          </p>
          <div class="set-field">
            <textarea
              class="set-textarea set-tel-greeting"
              rows="3"
              data-role="voicemail-input"
              maxlength="500"
            >${esc(settings.voicemailGreeting)}</textarea>
          </div>
          <div class="set-tel-actions">
            <button type="button" class="set-btn" data-action="save-voicemail">
              Update greeting
            </button>
          </div>
        </section>
      `;

      wireControls(platformNumber);
    }

    // ── Backend load ──────────────────────────────────────────────────────

    async function loadAssignment(): Promise<string> {
      const retailerId = ctx.user?.retailerId ?? "";
      if (!retailerId) return FALLBACK_NUMBER;
      try {
        const resp = await ctx.api<CallAssignmentResponse>(
          `/calls/assignment?retailerId=${encodeURIComponent(retailerId)}`,
          { method: "GET", signal: ac.signal },
        );
        const num = resp?.assignment?.platformNumber ?? "";
        return num || FALLBACK_NUMBER;
      } catch (err) {
        if (isAbortError(err)) throw err;
        // 401 / 404 / 5xx — always fall back, never block the panel.
        return FALLBACK_NUMBER;
      }
    }

    // ── Control wiring ─────────────────────────────────────────────────────

    function wireControls(platformNumber: string): void {
      // vCard download
      const vcardBtn = root.querySelector<HTMLButtonElement>(
        "[data-action='save-contact']",
      );
      vcardBtn?.addEventListener("click", () => onDownloadVCard(platformNumber));

      // Call forwarding
      const fwdToggle = root.querySelector<HTMLInputElement>(
        "[data-role='forward-toggle']",
      );
      const fwdField = root.querySelector<HTMLElement>(
        "[data-role='forward-field']",
      );
      const fwdInput = root.querySelector<HTMLInputElement>(
        "[data-role='forward-input']",
      );
      fwdToggle?.addEventListener("change", () => {
        settings.forwardEnabled = !!fwdToggle.checked;
        if (fwdField) fwdField.hidden = !settings.forwardEnabled;
        scheduleSave("forwarding", () => saveForwarding());
      });
      fwdInput?.addEventListener("input", () => {
        settings.forwardTo = fwdInput.value;
        scheduleSave("forwarding", () => saveForwarding());
      });
      const fwdWhen = root.querySelector<HTMLSelectElement>(
        "[data-role='forward-condition']",
      );
      fwdWhen?.addEventListener("change", () => {
        settings.forwardCondition = fwdWhen.value as ForwardCondition;
        scheduleSave("forwarding", () => saveForwarding());
      });

      // Call recording
      const recToggle = root.querySelector<HTMLInputElement>(
        "[data-role='recording-toggle']",
      );
      recToggle?.addEventListener("change", () => {
        settings.recordingEnabled = !!recToggle.checked;
        void saveRecording();
      });

      // Voicemail greeting
      const voicemailInput = root.querySelector<HTMLTextAreaElement>(
        "[data-role='voicemail-input']",
      );
      voicemailInput?.addEventListener("input", () => {
        settings.voicemailGreeting = voicemailInput.value;
      });
      const updateBtn = root.querySelector<HTMLButtonElement>(
        "[data-action='save-voicemail']",
      );
      updateBtn?.addEventListener("click", () => void saveVoicemail());
    }

    function scheduleSave(key: string, fn: () => Promise<void> | void): void {
      const prior = debounceTimers.get(key);
      if (prior !== undefined) window.clearTimeout(prior);
      const t = window.setTimeout(() => {
        debounceTimers.delete(key);
        if (disposed) return;
        void fn();
      }, 400);
      debounceTimers.set(key, t);
    }

    // ── vCard download ────────────────────────────────────────────────────

    function onDownloadVCard(phone: string): void {
      const vcf = buildVCard(phone);
      const blob = new Blob([vcf], { type: "text/vcard;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      objectUrls.add(url);

      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = "The_Kitchen_OS.vcf";
      document.body.appendChild(a);
      a.click();
      a.remove();

      // Revoke after a beat so the browser has time to start the download.
      window.setTimeout(() => {
        URL.revokeObjectURL(url);
        objectUrls.delete(url);
      }, 1000);

      ctx.showToast("Contact card downloaded", "success");
    }

    // ── Twilio saves ───────────────────────────────────────────────────────

    interface ForwardingResponse {
      ok?: boolean;
      status?: "active" | "pending_approval";
      message?: string;
      verifiedNumbers?: string[];
    }

    async function saveForwarding(): Promise<void> {
      const retailerId = ctx.user?.retailerId ?? "";
      try {
        const resp = await ctx.api<ForwardingResponse>("/portal/twilio/forwarding", {
          method: "PATCH",
          signal: ac.signal,
          body: JSON.stringify({
            retailerId,
            forwardTo: settings.forwardTo,
            enabled: settings.forwardEnabled,
            condition: settings.forwardCondition,
          }),
        });
        if (disposed) return;
        if (resp?.status === "pending_approval") {
          // Server held the number for admin approval and kept forwarding off.
          settings.forwardEnabled = false;
          const toggle = root.querySelector<HTMLInputElement>("[data-role='forward-toggle']");
          if (toggle) toggle.checked = false;
          ctx.showToast(
            resp.message || "Number sent for approval — forwarding stays off until approved.",
            "info",
          );
          return;
        }
        ctx.showToast("Call forwarding updated", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        if (isMissingEndpoint(err)) {
          ctx.showToast(
            "Call forwarding saved locally (server endpoint pending)",
            "info",
          );
          return;
        }
        ctx.showToast("Couldn't save call forwarding", "error");
      }
    }

    async function saveRecording(): Promise<void> {
      // TODO: PATCH /api/portal/twilio/recording
      try {
        await ctx.api("/portal/twilio/recording", {
          method: "PATCH",
          signal: ac.signal,
          body: JSON.stringify({ enabled: settings.recordingEnabled }),
        });
        if (disposed) return;
        ctx.showToast(
          settings.recordingEnabled
            ? "Call recording enabled"
            : "Call recording disabled",
          "success",
        );
      } catch (err) {
        if (isAbortError(err)) return;
        if (isMissingEndpoint(err)) {
          ctx.showToast(
            "Recording preference saved locally (server endpoint pending)",
            "info",
          );
          return;
        }
        ctx.showToast("Couldn't save recording preference", "error");
      }
    }

    async function saveVoicemail(): Promise<void> {
      const greeting = settings.voicemailGreeting.trim();
      if (!greeting) {
        ctx.showToast("Voicemail greeting can't be empty", "error");
        return;
      }
      // TODO: PUT /api/portal/twilio/voicemail
      try {
        await ctx.api("/portal/twilio/voicemail", {
          method: "PUT",
          signal: ac.signal,
          body: JSON.stringify({ greeting }),
        });
        if (disposed) return;
        ctx.showToast("Voicemail greeting updated", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        if (isMissingEndpoint(err)) {
          ctx.showToast(
            "Greeting saved locally (server endpoint pending)",
            "info",
          );
          return;
        }
        ctx.showToast("Couldn't save voicemail greeting", "error");
      }
    }

    // Hydrate forwarding config + verified numbers + recent forwarded-call log.
    // Best-effort: a missing endpoint just leaves the defaults in place.
    interface ForwardingState {
      forwardTo?: string;
      enabled?: boolean;
      condition?: ForwardCondition;
      verifiedNumbers?: string[];
      log?: ForwardedCall[];
    }
    async function loadForwarding(): Promise<void> {
      const retailerId = ctx.user?.retailerId ?? "";
      if (!retailerId) return;
      try {
        const resp = await ctx.api<ForwardingState>(
          `/portal/twilio/forwarding?retailerId=${encodeURIComponent(retailerId)}`,
          { method: "GET", signal: ac.signal },
        );
        if (disposed || !resp) return;
        settings.forwardTo = resp.forwardTo ?? settings.forwardTo;
        settings.forwardEnabled = resp.enabled ?? settings.forwardEnabled;
        if (resp.condition) settings.forwardCondition = resp.condition;
        verifiedNumbers = Array.isArray(resp.verifiedNumbers) ? resp.verifiedNumbers : [];
        forwardedLog = Array.isArray(resp.log) ? resp.log : [];
      } catch {
        /* endpoint missing / not authed — keep defaults */
      }
    }

    // ── Boot ──────────────────────────────────────────────────────────────

    void Promise.all([loadAssignment(), loadForwarding()])
      .then(([num]) => {
        if (disposed) return;
        renderPanel(num);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        if (disposed) return;
        renderPanel(FALLBACK_NUMBER);
      });

    // ── Cleanup ────────────────────────────────────────────────────────────
    return (): void => {
      disposed = true;
      ac.abort();
      for (const t of debounceTimers.values()) window.clearTimeout(t);
      debounceTimers.clear();
      for (const url of objectUrls) URL.revokeObjectURL(url);
      objectUrls.clear();
    };
  },
};

export default telephonyTab;
